// AUPRÉA — Modale « Suivi » du dossier (chronologie + relance manuelle)
import { useState, useEffect } from "react";
import axios from "axios";
import { C, Btn, Modal, Spinner, Alert, fmtEur } from "./shared";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: BACKEND + "/api" });
api.interceptors.request.use((c) => {
  const tok = localStorage.getItem("auprea_token");
  if (tok) c.headers.Authorization = `Bearer ${tok}`;
  return c;
});

const fmtDT = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return iso; }
};

const EVENT_LABELS = {
  proposition_ouverte: { color: C.teal, label: "Proposition ouverte par le client" },
  relance_auto:        { color: C.gold, label: "Relance automatique" },
  relance_envoyee:     { color: C.gold, label: "Relance envoyée" },
  relances_terminees:  { color: C.amber, label: "Relances terminées — à rappeler" },
  proposition_expiree: { color: C.red, label: "Proposition expirée" },
};

export const SuiviModal = ({ parcours, onClose }) => {
  const [suivi, setSuivi] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [relancing, setRelancing] = useState(false);

  const reload = () => {
    setLoading(true);
    api.get(`/parcours/${parcours.id}/suivi`)
      .then(r => setSuivi(r.data))
      .catch(e => setErr(e?.response?.data?.detail || "Erreur"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { reload(); }, []); // eslint-disable-line

  const relanceNow = async (canal) => {
    setRelancing(true);
    try {
      const r = await api.post(`/parcours/${parcours.id}/relance`, { canal, manuel: true });
      if (canal === "whatsapp") {
        const tel = parcours.fiche?.telephone?.replace(/\D/g, "") || "";
        const msg = encodeURIComponent(r.data.contenu);
        window.open(`https://wa.me/${tel}?text=${msg}`, "_blank");
      } else if (canal === "email") {
        window.open(`mailto:${parcours.fiche?.email || ""}?subject=Votre proposition AUPRÉA&body=${encodeURIComponent(r.data.contenu)}`, "_blank");
      }
      reload();
    } catch (e) { setErr(e?.response?.data?.detail || "Erreur"); }
    finally { setRelancing(false); }
  };

  const timeline = [];
  if (suivi?.date_publication) timeline.push({ type: "proposition_envoyee", at: suivi.date_publication, label: "Proposition envoyée" });
  if (suivi?.date_ouverture) timeline.push({ type: "proposition_ouverte", at: suivi.date_ouverture, label: "Proposition ouverte par le client" });
  (suivi?.events || []).forEach(e => timeline.push({ ...e, label: EVENT_LABELS[e.type]?.label || e.type, color: EVENT_LABELS[e.type]?.color }));
  (suivi?.relances_envoyees || []).forEach(r => timeline.push({ type: "relance_envoyee_row", at: r.at, label: `Relance ${r.manuel ? "manuelle" : "automatique"} ${r.canal.toUpperCase()}${r.j ? ` (J+${r.j})` : ""}`, canal: r.canal }));
  if (suivi?.statut_paiement === "paid") timeline.push({ type: "payee", at: parcours.payment_updated_at || suivi?.events?.slice(-1)[0]?.at, label: "Payée ✓", color: C.green });
  (suivi?.echeances || []).forEach((e, i) => timeline.push({ type: "echeance", at: e.date, label: `Échéance ${i+1}/3 · ${fmtEur(e.amount)} · ${e.status}`, color: e.status === "paid" ? C.green : C.red }));
  timeline.sort((a, b) => (a.at || "").localeCompare(b.at || ""));

  return (
    <Modal title={`Suivi · ${parcours.fiche?.prenom} ${parcours.fiche?.nom}`} onClose={onClose} wide
      footer={
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Btn onClick={onClose}>Fermer</Btn>
          {suivi?.statut_paiement !== "paid" && (
            <>
              <Btn small onClick={() => relanceNow("email")} disabled={relancing} data-testid="suivi-relance-email">📧 Relancer par e-mail</Btn>
              {parcours.fiche?.telephone && <Btn small variant="gold" onClick={() => relanceNow("whatsapp")} disabled={relancing} data-testid="suivi-relance-wa">💬 Relancer WhatsApp</Btn>}
            </>
          )}
        </div>
      }>
      {err && <Alert type="error"><span>⚠</span><span>{err}</span></Alert>}
      {loading ? <Spinner /> : (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 20 }}>
            <div style={{ background: C.gray2, borderRadius: 8, padding: 12 }}>
              <div style={{ fontSize: 10, color: C.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em" }}>Ouverture</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: suivi.date_ouverture ? C.green : C.muted, marginTop: 3 }}>
                {suivi.date_ouverture ? "Ouverte" : "Non ouverte"}
              </div>
              <div style={{ fontSize: 10, color: C.mid }}>{fmtDT(suivi.date_ouverture)}</div>
            </div>
            <div style={{ background: C.gray2, borderRadius: 8, padding: 12 }}>
              <div style={{ fontSize: 10, color: C.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em" }}>Paiement</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: suivi.statut_paiement === "paid" ? C.green : C.mid, marginTop: 3 }}>
                {suivi.statut_paiement === "paid" ? "Payée" : (suivi.echeances?.length ? `Échéance ${suivi.echeances.length}/3` : "En attente")}
              </div>
              {suivi.payment_mode && <div style={{ fontSize: 10, color: C.mid }}>{suivi.payment_mode === "trois_fois" ? "3 fois" : "comptant"}</div>}
            </div>
            <div style={{ background: C.gray2, borderRadius: 8, padding: 12 }}>
              <div style={{ fontSize: 10, color: C.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em" }}>Relances</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.navy, marginTop: 3 }}>{(suivi.relances_envoyees || []).length} envoyée(s)</div>
            </div>
          </div>

          <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: ".07em", marginBottom: 10 }}>Chronologie</div>
          {timeline.length === 0 ? <div style={{ fontSize: 12, color: C.muted, fontStyle: "italic" }}>Aucun événement pour le moment.</div> :
            timeline.map((ev, i) => (
              <div key={i} style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: 12, padding: "8px 0", borderBottom: `.5px dashed ${C.border}` }}>
                <div style={{ width: 8, height: 8, borderRadius: 4, background: ev.color || C.navy, marginTop: 6 }} />
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: C.navy }}>{ev.label}</div>
                  <div style={{ fontSize: 11, color: C.mid }}>{fmtDT(ev.at)}</div>
                </div>
              </div>
            ))
          }
        </>
      )}
    </Modal>
  );
};
